// schemas/user.ts
import { z } from 'zod'
import {
  ApiCollectionResponseSchema,
  ApiPaginatedResponseSchema,
  ApiSingleResponseSchema,
} from './api'
import type {
  ApiCollectionResponse,
  ApiPaginatedResponse,
  ApiSingleResponse,
} from './api'

// =====================================
// RELATED SCHEMAS
// =====================================

// Reference schemas for nested relations in role responses
const UserReferenceInRole = z.object({
  id: z.number(),
  name: z.string(),
  email: z.string().optional(),
  avatar: z.string().nullable().optional(),
  has_avatar: z.boolean().nullable().optional(),
})

const SubscriberReferenceInRole = z.object({
  id: z.number(),
  name: z.string(),
  short_name: z.string().optional(),
  is_active: z.boolean().optional(),
})

const ClientReferenceInRole = z.object({
  id: z.number(),
  name: z.string(),
  short_name: z.string().optional(),
  is_active: z.boolean().optional(),
})

// User Role Schema
export const UserRoleSchema = z.object({
  id: z.number(),
  user_id: z.number(),
  role: z.enum([
    'super-admin',
    'platform-admin',
    'agency-admin',
    'agency-user',
  ]),
  subscriber_id: z.number().nullable(),
  subscriber_name: z.string().nullable().optional(),
  client_id: z.number().nullable(),
  client_name: z.string().nullable().optional(),
  is_active: z.boolean(),
  expires_at: z.string().nullable(),
  context_name: z.string().optional(),
  context_description: z.string().optional(),
  user: UserReferenceInRole.optional(),
  subscriber: SubscriberReferenceInRole.nullable().optional(),
  client: ClientReferenceInRole.nullable().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
})

// =====================================
// MAIN USER SCHEMA
// =====================================

export const UserSchema = z.object({
  id: z.number(),
  name: z.string(),
  email: z.string().email(),
  phone: z.string().nullable().optional(),
  email_verified_at: z.string().nullable().optional(),
  is_active: z.boolean(),
  avatar: z.string().nullable().optional(),
  avatar_thumb: z.string().nullable().optional(),
  has_avatar: z.boolean().nullable().optional(),
  last_login_at: z.string().nullable().optional(),

  // Relations
  roles: z.array(UserRoleSchema).optional(),

  // Timestamps
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
})

export const UserReferenceSchema = z.object({
  id: z.number(),
  name: z.string(),
  email: z.string().email(),
  avatar: z.string().nullable().optional(),
})

export const PublicUserSchema = z.object({
  name: z.string().min(1).max(255),
  email: z.string().email(),
  phone: z.string().nullable().optional(),
  avatar: z.string().nullable().optional(),
})

export type User = z.infer<typeof UserSchema>
export type PublicUser = z.infer<typeof PublicUserSchema>
export type UserRole = z.infer<typeof UserRoleSchema>
export type UserReference = z.infer<typeof UserReferenceSchema>

// =====================================
// API RESPONSE SCHEMAS
// =====================================

// Single user response: { data: User }
export const UserApiResponseSchema = ApiSingleResponseSchema(UserSchema)
export type UserApiResponse = ApiSingleResponse<User>

// Multiple users response: { data: User[] }
export const UsersApiCollectionResponseSchema =
  ApiCollectionResponseSchema(UserSchema)
export type UsersApiCollectionResponse = ApiCollectionResponse<User>

// Paginated users response: { data: User[], meta: {...} }
export const UsersPaginatedApiResponseSchema =
  ApiPaginatedResponseSchema(UserSchema)
export type UsersPaginatedApiResponse = ApiPaginatedResponse<User>

// =====================================
// CRUD SCHEMAS
// =====================================

export const CreateUserSchema = z.object({
  name: z.string().min(1).max(255),
  email: z.string().email(),
  phone: z.string().optional(),
  password: z.string().min(8).optional(),
  role: UserRoleSchema.shape.role,
  subscriber_id: z.number().optional(),
  client_id: z.number().nullable().optional(),
  is_active: z.boolean().default(true),
})

export type CreateUser = z.infer<typeof CreateUserSchema>

export const UpdateUserSchema = CreateUserSchema.partial().extend({
  password: z.string().min(8).optional(),
})

export type UpdateUser = z.infer<typeof UpdateUserSchema>

// =====================================
// SPECIAL SCHEMAS
// =====================================

// Invite User Schema
export const InviteUserSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1),
  role: UserRoleSchema.shape.role,
  subscriber_id: z.number(),
  client_id: z.number().nullable().optional(),
  message: z.string().optional(),
})

export type InviteUser = z.infer<typeof InviteUserSchema>

// Invite Response Schema
export const InviteUserResponseSchema = ApiSingleResponseSchema(
  z.object({
    message: z.string(),
    invitation_sent: z.boolean(),
    user_id: z.number().optional(),
  })
)

export type InviteUserResponse = z.infer<typeof InviteUserResponseSchema>

// =====================================
// VALIDATION UTILITIES
// =====================================

// Direct validation functions
export const validateUser = (data: unknown): User => {
  return UserSchema.parse(data)
}

export const validateCreateUser = (data: unknown): CreateUser => {
  return CreateUserSchema.parse(data)
}

export const validateUpdateUser = (data: unknown): UpdateUser => {
  return UpdateUserSchema.parse(data)
}

export const validateInviteUser = (data: unknown): InviteUser => {
  return InviteUserSchema.parse(data)
}

// API response validation functions
export const validateUserApiResponse = (data: unknown): User => {
  const response = UserApiResponseSchema.parse(data)
  return response.data
}

export const validateUsersPaginatedApiResponse = (
  data: unknown
): UsersPaginatedApiResponse => {
  return UsersPaginatedApiResponseSchema.parse(data)
}

// Safe validation functions
export const safeValidateUser = (data: unknown) => {
  return UserSchema.safeParse(data)
}

export const safeValidateUserApiResponse = (data: unknown) => {
  const result = UserApiResponseSchema.safeParse(data)
  if (result.success) {
    return { success: true, data: result.data.data }
  }
  return { success: false, error: result.error }
}

// =====================================
// FILTER SCHEMA
// =====================================

export const UserFilterSchema = z.object({
  search: z.string().optional(),
  role: z.string().optional(),
  subscriber_id: z.number().optional(),
  client_id: z.number().nullable().optional(),
  is_active: z.boolean().optional(),
})

export type UserFilter = z.infer<typeof UserFilterSchema>
